import { PRICES } from './prices.ts';
import { money, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localPrice } from './character.ts';
import { policyShare } from './policy.ts';
import { homeCountry, type Country } from './travel.ts';
import { Rng } from './rng.ts';

/**
 * Cars you own. A car belongs to a life and to the country it was bought in: it's parked somewhere (at home,
 * on a street, at work, at the airport), you can only drive it from where it is, and it can't follow you
 * abroad. Driving costs fuel. The makes are made up (marques.ts in the game draws their faces).
 */

export type MakeId = 'kodo' | 'pelleton' | 'cartwright' | 'bayan' | 'lumen' | 'volta' | 'aurelio' | 'sora';
export const MAKES: Record<MakeId, { name: string; from: Country | 'germany' }> = {
  kodo: { name: 'Kodo', from: 'japan' },
  pelleton: { name: 'Pelleton', from: 'france' },
  cartwright: { name: 'Cartwright', from: 'usa' },
  bayan: { name: 'Bayan', from: 'china' },
  lumen: { name: 'Lumen', from: 'germany' },
  volta: { name: 'Volta', from: 'usa' },
  aurelio: { name: 'Aurelio', from: 'italy' },
  sora: { name: 'Sora', from: 'japan' },
};

export interface CarModel {
  id: string;
  make: MakeId;
  name: string;
  body: 'sedan' | 'suv' | 'bike' | 'super';
  /** a sports bike (a faired superbike, ridden tucked in), not a commuter */
  sport?: boolean;
  /** a body of its own (the game's vehicles.ts draws it), not its make's usual saloon or SUV */
  shape?: 'ev' | 'wedge' | 'hyper' | 'hyperspider' | 'furia' | 'roadster' | 'cabrio' | 'muscle' | 'speedster' | 'hatch' | 'compact' | 'bubble' | 'apex' | 'flyline' | 'pickup' | 'monte' | 'celeste' | 'maestro';
  /** a convertible: the roof down */
  open?: boolean;
  /** a convertible whose roof goes up and down (a button): its body with the roof down */
  roof?: 'celesteopen' | 'maestroopen';
  /** new, in Lagos-sized naira (localPrice turns it into a price where you are) */
  price: number;
  note: string;
}

export const CAR_MODELS: CarModel[] = [
  { id: 'kodo-corsa', make: 'kodo', name: 'Corsa', body: 'sedan', price: 24_000_000, note: 'The saloon everybody has: it never breaks, and if it does, every mechanic knows it.' },
  { id: 'kodo-highland', make: 'kodo', name: 'Highland', body: 'suv', price: 52_000_000, note: 'Seven seats, high up over the potholes.' },
  { id: 'pelleton-406', make: 'pelleton', name: '406', body: 'sedan', price: 16_000_000, note: 'Soft ride, cheap parts, a classic.' },
  { id: 'pelleton-rive', make: 'pelleton', name: 'Rive', body: 'suv', price: 40_000_000, note: 'French and comfortable, a little delicate.' },
  { id: 'cartwright-meridian', make: 'cartwright', name: 'Meridian', body: 'sedan', price: 28_000_000, note: 'Big, quiet, made for long straight roads.' },
  { id: 'cartwright-prairie', make: 'cartwright', name: 'Prairie', body: 'suv', price: 58_000_000, note: 'Room for everyone and everything.' },
  { id: 'bayan-lan', make: 'bayan', name: 'Lan', body: 'sedan', price: 22_000_000, note: 'Electric: a screen the size of a laptop, plug it in at night.' },
  { id: 'bayan-shan', make: 'bayan', name: 'Shan', body: 'suv', price: 42_000_000, note: 'An electric SUV, quick and silent.' },
  { id: 'lumen-s5', make: 'lumen', name: 'S5', body: 'sedan', price: 70_000_000, note: 'Leather and quiet: the car that says you made it.' },
  { id: 'lumen-r7', make: 'lumen', name: 'R7', body: 'suv', price: 110_000_000, note: 'The big-man car. People open the gate faster.' },
  // the cheap ones: what most people can afford
  { id: 'pelleton-bulle', make: 'pelleton', name: 'Bulle', body: 'sedan', shape: 'bubble', price: 3_800_000, note: 'Forty years old and round as an egg, the engine in the back. The cheapest car you can own, and everyone smiles at it.' },
  { id: 'sora-mira', make: 'sora', name: 'Mira', body: 'sedan', shape: 'hatch', price: 6_500_000, note: 'Small, slow and honest: it parks anywhere and sips fuel. The cheapest new car in the showroom.' },
  { id: 'sora-sunny', make: 'sora', name: 'Sunny', body: 'sedan', shape: 'compact', price: 9_500_000, note: 'A plain little saloon: cloth seats, a big boot, and parts in every market. The first car of half the city.' },
  // the electric start-up
  { id: 'volta-arc', make: 'volta', name: 'Arc', body: 'sedan', shape: 'ev', price: 68_000_000, note: 'No grille, no engine, no noise: a glass roof end to end and a screen where the buttons were. It updates itself overnight.' },
  { id: 'lumen-gt6', make: 'lumen', name: 'GT6', body: 'super', shape: 'flyline', price: 220_000_000, note: 'The engine in the back, round lamps up on the wings, the roof one line down to the tail. Sixty years of the same shape, and still the one everyone knows.' },
  { id: 'volta-apex', make: 'volta', name: 'Apex', body: 'super', shape: 'apex', price: 380_000_000, note: 'An electric roadster: no grille, cat\'s-eye lamps, a glass roof that lifts off. 0 to 100 in under two seconds, and not a sound.' },
  { id: 'kodo-trekker', make: 'kodo', name: 'Trekker', body: 'suv', shape: 'pickup', price: 75_000_000, note: 'A pickup built for bad roads: high off the ground, an open bed for anything you need to carry, and a chassis that shrugs off potholes. It will outlive you.' },
  { id: 'volta-ridge', make: 'volta', name: 'Ridge', body: 'suv', shape: 'wedge', price: 150_000_000, note: 'A stainless-steel wedge of a pickup: no curves anywhere, a bar of light across each end. People film it as it passes.' },
  // convertibles: the roof down
  { id: 'kodo-kaze', make: 'kodo', name: 'Kaze', body: 'sedan', shape: 'roadster', open: true, price: 34_000_000, note: 'A little two-seat roadster: light, simple, the roof folds away in ten seconds. The most fun you can have for the money.' },
  { id: 'cartwright-stallion', make: 'cartwright', name: 'Stallion', body: 'sedan', shape: 'muscle', open: true, price: 78_000_000, note: 'A V8 convertible with a bonnet a mile long: top down, the radio up, a Sunday on the coast road.' },
  { id: 'pelleton-riviera', make: 'pelleton', name: 'Riviera', body: 'sedan', shape: 'speedster', open: true, price: 120_000_000, note: 'A 1960s speedster, restored: chrome, round lamps, a screen you look over. A film star’s car.' },
  { id: 'lumen-c8', make: 'lumen', name: 'C8 Cabriolet', body: 'sedan', shape: 'cabrio', open: true, price: 165_000_000, note: 'Four seats, leather, the roof gone at the touch of a button: the car for being seen on the Lekki coast road.' },
  // the hypercar: built by hand, a few a year
  { id: 'aurelio-furia', make: 'aurelio', name: 'Furia V12', body: 'super', shape: 'furia', price: 820_000_000, note: 'An Italian V12 wedge, as low and sharp as a car gets: Y-shaped lamps, doors that swing up, a wing on stalks and a hexagon for an exhaust. You hear it before you see it.' },
  { id: 'aurelio-vento', make: 'aurelio', name: 'Vento', body: 'super', shape: 'hyper', price: 1_300_000_000, note: 'Built by hand in Italy, a few a year: a glass bubble for a cabin, a V12 behind you and four pipes in a circle.' },
  { id: 'aurelio-monte', make: 'aurelio', name: 'Monte', body: 'suv', shape: 'monte', price: 450_000_000, note: 'The Italian super-SUV: a V8 with 650 horsepower, a coupé\u2019s roof, ground clearance for the potholes. Angular lamps, a mouth like a shark, four pipes.' },
  { id: 'kodo-celeste', make: 'kodo', name: 'Celeste C', body: 'sedan', shape: 'celeste', roof: 'celesteopen', price: 72_000_000, note: 'A grand tourer with a folding metal roof: a quiet four-seat coupé, then twenty seconds and a button, and it\u2019s a convertible. Smooth, unshowy, built to last.' },
  { id: 'lumen-maestro', make: 'lumen', name: 'Maestro SL', body: 'sedan', shape: 'maestro', roof: 'maestroopen', price: 520_000_000, note: 'The long open tourer from the most expensive name there is: a V12, an upright chrome grille, a fabric top that folds away at the touch of a button, four seats in hand-stitched leather.' },
  { id: 'aurelio-vento-spider', make: 'aurelio', name: 'Vento Spider', body: 'super', shape: 'hyperspider', open: true, price: 1_500_000_000, note: 'The Vento with the roof off: the V12 right behind your head, and the whole street looking in.' },
  // two wheels
  // supercars: low, loud, two seats, more money than a house
  { id: 'kodo-raiden', make: 'kodo', name: 'Raiden GT', body: 'super', price: 240_000_000, note: 'A twin-turbo coupé from the company that makes the sensible cars: 600 horsepower, and it still starts every morning.' },
  { id: 'cartwright-thunder', make: 'cartwright', name: 'Thunder V8', body: 'super', price: 310_000_000, note: 'A V8 you hear two streets away. Built for straight roads and being looked at.' },
  { id: 'bayan-feng', make: 'bayan', name: 'Feng', body: 'super', price: 480_000_000, note: 'An electric hypercar: no sound at all, then 100 km/h in two seconds. The doors open upwards.' },
  { id: 'lumen-vx', make: 'lumen', name: 'VX12', body: 'super', price: 650_000_000, note: 'A V12 with a carbon wing. In Dubai it is just a car; anywhere else it stops traffic.' },
  { id: 'kodo-swift', make: 'kodo', name: 'Swift 125', body: 'bike', price: 1_900_000, note: 'The bike every okada man rides: cheap to run, starts every morning, goes through any traffic.' },
  { id: 'bayan-dian', make: 'bayan', name: 'Dian', body: 'bike', price: 1_500_000, note: 'An electric scooter: silent, cheap to charge, made for the city.' },
  { id: 'pelleton-ville', make: 'pelleton', name: 'Ville 125', body: 'bike', price: 3_600_000, note: 'A city scooter with a top box: park it anywhere.' },
  { id: 'cartwright-roadster', make: 'cartwright', name: 'Roadster 750', body: 'bike', price: 16_000_000, note: 'A big, loud motorcycle. Not practical. Wonderful.' },
  { id: 'lumen-rs', make: 'lumen', name: 'RS 1000', body: 'bike', sport: true, price: 26_000_000, note: 'A superbike: a full fairing, 200 horsepower, and a riding position that tells everyone you mean it.' },
];

export const CAR_COLOURS: [number, string][] = [[0xc8c8c8, 'silver'], [0x1a1a1a, 'black'], [0x6b0f1a, 'wine'], [0x1e3a6b, 'blue'], [0xe8e8e8, 'white'], [0x2f4f2f, 'green'], [0x4fae2c, 'lime']];

export interface OwnedVehicle {
  id: string;
  model: string;
  colour: number;
  colourName: string;
  /** where it's registered: it can't leave the country */
  country: Country;
  /** where it's parked: 'home', 'street:<hood>', 'work', 'place:<name>', 'airport:<id>' */
  at: string;
  /** at home, put away in the garage (not out at the kerb) */
  inGarage?: boolean;
  /** where on that road (road-local kerb position), so it's where you left it */
  spot?: { road: number; x: number; side: 1 | -1 };
  bought: number;
  paid: number;
  /** (a car whose roof goes up and down) it's down now */
  roofOpen?: boolean;
  /** km driven */
  km: number;
  /** papers (vehicle licence, roadworthiness; in Nigeria third-party insurance too) good until (minutes) */
  papersUntil?: number;
  /** the odometer at the last service */
  servicedKm?: number;
  /** monthly insurance (outside Nigeria): lapsed when a month couldn't be paid */
  uninsured?: boolean;
}

export const modelOf = (v: OwnedVehicle): CarModel => CAR_MODELS.find((m) => m.id === v.model) ?? CAR_MODELS[0];
/** the body it has now (a folding roof: up or down) */
export const shapeOf = (v: OwnedVehicle) => { const m = modelOf(v); return v.roofOpen && m.roof ? m.roof : m.shape; };
/** its roof goes up and down */
export const hasRoofButton = (v: OwnedVehicle) => !!modelOf(v).roof;
/** "your silver Kodo Corsa" */
export const carName = (v: OwnedVehicle) => `${MAKES[modelOf(v).make].name} ${modelOf(v).name}`;

/** The cars you own (none yet: []). */
export function vehiclesOf(s: LifeState): OwnedVehicle[] { return (s.vehicles ??= []); }

/** Your car that's here in this country and parked where you are (`at`), if any. */
export function carAt(s: LifeState, at: string): OwnedVehicle | null {
  const c = hereCountry(s);
  return vehiclesOf(s).find((v) => v.country === c && v.at === at) ?? null;
}

/** How many cars a home keeps under cover (the rest wait at the kerb outside): by how grand it is. No garage at all for a room. */
export function garageSpaces(homeId: string): number {
  if (/estate|mansion|villa|penthouse|luxury|lux/i.test(homeId)) return 8;
  if (/owned|house|duplex|colonial|country|terrace|bungalow/i.test(homeId)) return 3;
  if (/family|flat|apartment/i.test(homeId)) return 1;
  return 0;
}
/** How many of your cars stand out at the kerb at home: the rest go in the garage, while there's room */
export const KERB_MAX = 3;
/** your cars at home: in the garage and out at the kerb, the garage's size; tidied as it goes (a car driven off isn't in it; the kerb keeps to KERB_MAX while the garage has room) */
export function garageOf(s: LifeState): { cap: number; inside: OwnedVehicle[]; kerb: OwnedVehicle[] } {
  const cap = garageSpaces(s.card.home.id), home = vehiclesOf(s).filter((v) => v.at === 'home');
  for (const v of vehiclesOf(s)) if (v.at !== 'home' && v.inGarage) delete v.inGarage;
  let inside = home.filter((v) => v.inGarage);
  for (const v of inside.slice(cap)) delete v.inGarage; // (a smaller home: what doesn't fit goes back out)
  inside = inside.slice(0, cap);
  let kerb = home.filter((v) => !v.inGarage);
  for (const v of kerb.slice(KERB_MAX)) { if (inside.length >= cap) break; v.inGarage = true; inside.push(v); }
  kerb = home.filter((v) => !v.inGarage);
  return { cap, inside, kerb };
}
/** out of the garage to the kerb; if the kerb's full, the car that's been out longest goes in for it */
export function bringOut(s: LifeState, id: string): boolean {
  const v = vehiclesOf(s).find((x) => x.id === id && x.at === 'home' && x.inGarage);
  if (!v) return false;
  delete v.inGarage;
  // (the swap first: the tidy after it would otherwise put this one straight back)
  const kerb = vehiclesOf(s).filter((x) => x.at === 'home' && !x.inGarage);
  if (kerb.length > KERB_MAX) { const out = kerb.find((x) => x.id !== id); if (out) out.inGarage = true; }
  garageOf(s);
  return true;
}
/** from the kerb into the garage, if there's a space */
export function putAway(s: LifeState, id: string): boolean {
  const g = garageOf(s), v = g.kerb.find((x) => x.id === id);
  if (!v || g.inside.length >= g.cap) return false;
  v.inGarage = true; return true;
}
/** the cars you've chosen to go together: only what you still own, here in this country. They're your cars, not copies */
export const CONVOY_MAX = 6;
export function convoyOf(s: LifeState): OwnedVehicle[] {
  const c = hereCountry(s), ids = s.convoy ?? [];
  return ids.map((id) => vehiclesOf(s).find((v) => v.id === id && v.country === c)).filter((v): v is OwnedVehicle => !!v).slice(0, CONVOY_MAX);
}
/** (up to CONVOY_MAX in each country: a convoy chosen in Lagos used to fill the list, so one abroad showed nothing ticked and took nothing) */
export function setConvoy(s: LifeState, ids: string[]) {
  const per = new Map<string, number>();
  s.convoy = [...new Set(ids)].filter((id) => { const v = vehiclesOf(s).find((x) => x.id === id); if (!v) return false; const n = per.get(v.country) ?? 0; per.set(v.country, n + 1); return n < CONVOY_MAX; });
}

/** Your cars in this country (wherever they're parked). */
export function carsHere(s: LifeState): OwnedVehicle[] { const c = hereCountry(s); return vehiclesOf(s).filter((v) => v.country === c); }

/**
 * Cars are traded goods: a price is the Lagos price at the exchange rate, times what the market there adds or
 * takes off (import duty makes Lagos dear; Chinese cars are cheap in China), and a car made in the country is
 * cheaper there. (Not localPrice: that scales labour-priced things, which would make a car in New York cost five
 * times one in Lagos.)
 */
const MARKET: Record<Country, number> = { nigeria: 1, usa: 1.45, france: 1.55, china: 1.05, japan: 1.25, italy: 1.5, uae: 1.15 };
export function carPrice(s: LifeState, model: string): number {
  const m = CAR_MODELS.find((x) => x.id === model);
  if (!m) return 0;
  const c = hereCountry(s), n = m.price * MARKET[c] * (MAKES[m.make].from === c ? 0.82 : 1) * PRICES;
  // round to two significant figures in the money people see here
  const rate = s.card.currency.rate ?? 1, v = n / rate, step = 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1);
  return Math.round(v / step) * step * rate;
}

/** Buy a car here. It's parked `at` (the showroom: you drive it away; or delivered home). Returns an error, or null. */
export function buyCar(s: LifeState, model: string, colour: number, at = 'home'): string | null {
  const m = CAR_MODELS.find((x) => x.id === model);
  if (!m) return 'No such car.';
  const price = carPrice(s, model);
  const name = CAR_COLOURS.find(([c]) => c === colour)?.[1] ?? 'silver';
  if (!spend(s, price, `${MAKES[m.make].name} ${m.name}`)) return "You can't afford it.";
  vehiclesOf(s).push({ id: `car-${s.minutes}-${vehiclesOf(s).length}-${Math.random().toString(36).slice(2, 6)}`, model, colour, colourName: name, country: hereCountry(s), at, bought: s.minutes, paid: price, km: 0, papersUntil: s.minutes + YEAR, servicedKm: 0 }); // (the dealer does the first papers)
  s.history.push({ t: s.minutes, text: `Bought a ${name} ${MAKES[m.make].name} ${m.name}.` });
  return null;
}

/** Fuel for a drive of so many minutes (stop-start city driving). Electric cars charge for much less. */
export function fuelCost(s: LifeState, v: OwnedVehicle, minutes: number): number {
  const m = modelOf(v), per = (m.body === 'suv' ? 55 : m.body === 'bike' ? (m.id === 'cartwright-roadster' || m.sport ? 22 : 12) : 40) * (m.make === 'bayan' ? 0.35 : 1);
  return localPrice(s, per * minutes * (serviceDue(v) > 0 ? 1.15 : 1) * policyShare(hereCountry(s), 'fuel_price'), 100);
}

/** You drove it somewhere: pay for fuel, clock the km, and it's parked there now. */
export function drove(s: LifeState, v: OwnedVehicle, minutes: number, to: string): number {
  const cost = Math.min(s.money, fuelCost(s, v, minutes));
  if (cost > 0) spend(s, cost, modelOf(v).make === 'bayan' ? 'Charging' : 'Fuel');
  v.km += Math.round(minutes * 0.45);
  v.at = to;
  v.spot = undefined;
  return cost;
}

// --- running it: papers, insurance, servicing (all visible in the Car app; each one says what it costs) ---------

const DAY = 1440, YEAR = 365 * DAY, SERVICE_KM = 10_000;

/** Papers for a year: vehicle licence + roadworthiness (+ third-party insurance in Nigeria). */
export function papersPrice(s: LifeState, v: OwnedVehicle): number {
  const m = modelOf(v);
  return localPrice(s, m.body === 'bike' ? 18_000 : m.body === 'suv' ? 65_000 : 45_000, 500);
}
/** Days of papers left (negative: expired that many days ago). */
export const papersLeft = (s: LifeState, v: OwnedVehicle) => Math.floor(((v.papersUntil ?? s.minutes + YEAR) - s.minutes) / DAY);
export function renewPapers(s: LifeState, v: OwnedVehicle): boolean {
  if (!spend(s, papersPrice(s, v), `Papers: ${carName(v)}`)) return false;
  v.papersUntil = Math.max(s.minutes, v.papersUntil ?? s.minutes) + YEAR;
  return true;
}

/** Monthly insurance (comprehensive, outside Nigeria: there it's third-party, inside the papers). 0: none due. */
export function insurancePrice(s: LifeState, v: OwnedVehicle): number {
  if (v.country === 'nigeria') return 0;
  const n = carPrice(s, v.model) * 0.0035, rate = s.card.currency.rate ?? 1;
  return Math.round(n / rate / 5) * 5 * rate;
}

/** km past the service (negative: that many km to go). */
export const serviceDue = (v: OwnedVehicle) => v.km - ((v.servicedKm ?? v.km) + SERVICE_KM);
export function servicePrice(s: LifeState, v: OwnedVehicle): number {
  const m = modelOf(v);
  return localPrice(s, m.body === 'bike' ? 12_000 : m.body === 'suv' ? 60_000 : 38_000, 500);
}
export function serviceCar(s: LifeState, v: OwnedVehicle): boolean {
  if (!spend(s, servicePrice(s, v), `Service: ${carName(v)}`)) return false;
  v.servicedKm = v.km;
  return true;
}

/**
 * A checkpoint on the way (expired papers or lapsed insurance only: with everything in order they wave you on).
 * Nigeria: more checkpoints, and the officer offers to "settle" for less than the fine. Elsewhere: a ticket.
 */
export interface Checkpoint { fine: number; settle: number | null; why: string }
export function checkpoint(s: LifeState, v: OwnedVehicle, salt: number): Checkpoint | null {
  const expired = papersLeft(s, v) < 0, lapsed = !!v.uninsured;
  if (!expired && !lapsed) return null;
  const ng = v.country === 'nigeria';
  if (!new Rng((s.seed ^ Math.imul(salt + 17, 0x9e3779b1)) >>> 0).chance(ng ? 0.35 : 0.15)) return null;
  const why = expired ? 'Your papers have expired' : 'No insurance';
  return { fine: localPrice(s, expired ? 25_000 : 40_000, 500), settle: ng ? localPrice(s, 5_000, 500) : null, why };
}
export function payCheckpoint(s: LifeState, c: Checkpoint, settle: boolean) {
  const amt = Math.min(s.money, settle && c.settle ? c.settle : c.fine);
  spend(s, amt, settle ? 'Checkpoint "settlement"' : 'Traffic fine');
  if (!settle) s.history.push({ t: s.minutes, text: `Fined at a checkpoint: ${c.why.toLowerCase()}.` });
}

/** Each day: reminders a week before the papers run out and the day they do; insurance on the 1st; the service light. */
export function carsDay(s: LifeState, events: LifeEvent[]) {
  const first = new Date(s.minutes * 60_000).getUTCDate() === 1;
  for (const v of vehiclesOf(s)) {
    const left = papersLeft(s, v);
    if (left === 7 || left === 0) events.push({ t: s.minutes, kind: 'info', text: left ? `Your ${carName(v)}'s papers run out in a week (${money(s, papersPrice(s, v))} to renew, in the Car app).` : `Your ${carName(v)}'s papers ran out today. Checkpoints will stop you.` });
    if (first) {
      const ins = insurancePrice(s, v);
      if (ins > 0) {
        if (spend(s, ins, `Insurance: ${carName(v)}`)) { if (v.uninsured) events.push({ t: s.minutes, kind: 'info', text: `Your ${carName(v)} is insured again.` }); v.uninsured = false; }
        else if (!v.uninsured) { v.uninsured = true; events.push({ t: s.minutes, kind: 'info', text: `Couldn't pay the insurance on your ${carName(v)} (${money(s, ins)}). It's lapsed: driving it uninsured is a fine.` }); }
      }
    }
    if (serviceDue(v) > 0 && serviceDue(v) < 60) events.push({ t: s.minutes, kind: 'info', text: `The service light is on in your ${carName(v)}. Overdue, it burns more fuel and is worth less.` });
  }
}

/**
 * Some lives start with a car: a wealthy grown-up has one (an SUV in Lagos), a comfortable one sometimes has a
 * saloon. Called once when a life starts.
 */
export function starterCar(s: LifeState) {
  if (s.card.stage === 'young' || vehiclesOf(s).length) return;
  const bg = s.card.background.id, rng = new Rng(s.seed * 7 + 13); // (Rng takes a number)
  const want = bg === 'wealthy' ? 'suv' : bg === 'comfortable' && rng.chance(0.5) ? 'sedan' : bg === 'working' && homeCountry(s) === 'nigeria' && rng.chance(0.15) ? 'bike' : null;
  if (!want) return;
  const country = homeCountry(s);
  const home: Partial<Record<Country, MakeId>> = { nigeria: 'kodo', france: 'pelleton', usa: 'cartwright', china: 'bayan', japan: 'kodo' };
  const make = bg === 'wealthy' && country === 'nigeria' ? 'lumen' : home[country] ?? 'kodo';
  const model = want === 'bike' ? CAR_MODELS.find((m) => m.id === 'kodo-swift')! : CAR_MODELS.find((m) => m.make === make && m.body === want) ?? CAR_MODELS[0];
  const [colour, colourName] = CAR_COLOURS[rng.int(0, CAR_COLOURS.length - 1)];
  const km = rng.int(8_000, 90_000);
  vehiclesOf(s).push({ id: 'car-start', model: model.id, colour, colourName, country, at: 'home', bought: 0, paid: 0, km, papersUntil: s.minutes + rng.int(20, 300) * DAY, servicedKm: km - rng.int(1_000, 9_000) });
}

/**
 * What a dealer gives you for it: what it would cost new here, less a fifth the day it leaves the lot, less
 * more for every 10,000 km (never under a third).
 */
export function carValue(s: LifeState, v: OwnedVehicle): number {
  const fresh = carPrice(s, v.model);
  const k = Math.max(0.33, 0.8 - (v.km / 10_000) * 0.04) * (serviceDue(v) > 0 ? 0.95 : 1);
  const rate = s.card.currency.rate ?? 1, n = (fresh * k) / rate, step = 10 ** Math.max(0, Math.floor(Math.log10(n)) - 1);
  return Math.round(n / step) * step * rate;
}

/** Sell it back to the dealer. Returns what you got (0: no such car, or it's in another country). */
export function sellCar(s: LifeState, id: string): number {
  const list = vehiclesOf(s), i = list.findIndex((v) => v.id === id);
  if (i < 0 || list[i].country !== hereCountry(s)) return 0;
  const v = list[i], got = carValue(s, v);
  list.splice(i, 1);
  s.money += got;
  s.ledger.push({ t: s.minutes, amount: got, label: `Sold the ${carName(v)}` });
  s.history.push({ t: s.minutes, text: `Sold the ${v.colourName} ${carName(v)}.` });
  return got;
}

/** What's on the showroom floor in each country: its own makes first, then what sells there. */
const STOCK: Record<Country, string[]> = {
  nigeria: ['sora-mira', 'kodo-corsa', 'kodo-trekker', 'kodo-kaze', 'kodo-celeste', 'lumen-gt6', 'lumen-c8', 'lumen-maestro', 'aurelio-monte', 'aurelio-furia'],
  france: ['pelleton-bulle', 'pelleton-406', 'pelleton-riviera', 'lumen-gt6', 'lumen-c8', 'lumen-maestro', 'volta-arc'],
  italy: ['aurelio-furia', 'aurelio-monte', 'aurelio-vento', 'aurelio-vento-spider', 'pelleton-406', 'pelleton-riviera', 'kodo-kaze', 'lumen-c8'],
  uae: ['aurelio-furia', 'aurelio-monte', 'aurelio-vento', 'lumen-maestro', 'volta-apex', 'lumen-vx', 'bayan-feng', 'volta-ridge', 'aurelio-vento-spider'],
  usa: ['volta-apex', 'volta-arc', 'volta-ridge', 'cartwright-stallion', 'cartwright-prairie', 'kodo-kaze', 'kodo-celeste', 'aurelio-monte', 'lumen-maestro'],
  china: ['bayan-lan', 'bayan-shan', 'kodo-corsa', 'lumen-s5', 'lumen-r7', 'lumen-maestro', 'pelleton-rive'],
  japan: ['sora-mira', 'sora-sunny', 'kodo-corsa', 'kodo-kaze', 'kodo-celeste', 'kodo-highland', 'lumen-s5'],
};
export function dealerStock(s: LifeState): CarModel[] { return [...STOCK[hereCountry(s)], ...BIKES[hereCountry(s)]].map((id) => CAR_MODELS.find((m) => m.id === id)!); }
const BIKES: Record<Country, string[]> = {
  nigeria: ['kodo-swift', 'bayan-dian', 'lumen-rs'],
  france: ['pelleton-ville', 'kodo-swift', 'lumen-rs'],
  italy: ['pelleton-ville', 'kodo-swift', 'lumen-rs'],
  uae: ['kodo-swift', 'lumen-rs', 'cartwright-roadster'],
  usa: ['cartwright-roadster', 'kodo-swift', 'lumen-rs'],
  china: ['bayan-dian', 'kodo-swift', 'lumen-rs'],
  japan: ['kodo-swift', 'bayan-dian', 'lumen-rs'],
};

// --- a lift from someone you know -------------------------------------------------------------------

/** People you could ask for a lift: family and friends, partners. */
const LIFT_FROM = ['Friend', 'Brother', 'Sister', 'Father', 'Mother', 'Wife', 'Husband', 'Girlfriend', 'Boyfriend', 'Cousin', 'Uncle', 'Aunt'];
const hash = (t: string) => { let h = 2166136261; for (let i = 0; i < t.length; i++) h = Math.imul(h ^ t.charCodeAt(i), 16777619); return h >>> 0; };

/** Does this person have a car? (Fixed per person: more likely in a comfortable family, and for parents.) */
export function hasCar(s: LifeState, name: string, relation: string): boolean {
  const bg = s.card.background.id, base = bg === 'wealthy' ? 80 : bg === 'comfortable' ? 55 : bg === 'working' ? 30 : 18;
  return hash(name) % 100 < base + (relation === 'Father' || relation === 'Mother' ? 15 : 0);
}

export interface Lift { name: string; first: string; relation: string; model: CarModel; colour: [number, string]; ok: boolean; reason?: string }

/**
 * Who could come and get you: someone close enough to ask (closeness 50+) who has a car, here in this country.
 * The first one free to come, else the first one who can't, with why ("at work until 5"); null if nobody has a car.
 */
export function liftFor(s: LifeState): Lift | null {
  if (hereCountry(s) !== homeCountry(s)) return null; // (abroad: they're all at home)
  const lifts = (s.lifts ??= {});
  const day = Math.floor(s.minutes / 1440), hour = (s.minutes % 1440) / 60, weekday = new Date(Date.UTC(2026, 9, 12) + day * 864e5).getUTCDay();
  const work = weekday >= 1 && weekday <= 5 && hour >= 8.5 && hour < 17;
  const cands = s.contacts.filter((c) => LIFT_FROM.includes(c.relation) && c.closeness >= 50 && hasCar(s, c.name, c.relation)).sort((a, b) => b.closeness - a.closeness);
  if (!cands.length) return null;
  const mk = (c: (typeof cands)[number], ok: boolean, reason?: string): Lift => {
    const h = hash(c.name), local = CAR_MODELS.filter((m) => m.body !== 'bike' && (MAKES[m.make].from === homeCountry(s) || m.price < 40_000_000));
    return { name: c.name, first: c.name.split(' ')[0], relation: c.relation, model: local[h % local.length], colour: CAR_COLOURS[(h >>> 8) % CAR_COLOURS.length], ok, reason };
  };
  for (const c of cands) {
    if (lifts[c.name] === day) continue;
    if (hour >= 22 || hour < 6) continue;
    if (work && !['Wife', 'Husband'].includes(c.relation)) continue;
    return mk(c, true);
  }
  const c = cands[0];
  return mk(c, false, hour >= 22 || hour < 6 ? `${c.name.split(' ')[0]} is asleep by now` : lifts[c.name] === day ? `${c.name.split(' ')[0]} already gave you a lift today` : `${c.name.split(' ')[0]} is at work until 5`);
}

/** They came: it's a favour (a little closeness spent), and you won't ask them twice in a day. */
export function tookLift(s: LifeState, name: string) {
  (s.lifts ??= {})[name] = Math.floor(s.minutes / 1440);
  const c = s.contacts.find((x) => x.name === name);
  if (c) c.closeness = Math.max(0, c.closeness - 1);
}

/** You kept them waiting and they gave up: that costs more. */
export function stoodUp(s: LifeState, name: string) {
  const c = s.contacts.find((x) => x.name === name);
  if (c) c.closeness = Math.max(0, c.closeness - 4);
}
